class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // --- ORIGINAL CONSTRUCTOR LOGIC (unchanged) ---
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    const iconsearch = document.querySelector(".header__search");
    if (iconsearch)
      iconsearch.addEventListener("click", () => {
        this.classList.contains("active") && this.close();
      });

    const iconaccount = document.querySelector(".header__icon--account");
    if (iconaccount)
      iconaccount.addEventListener("click", () => {
        this.classList.contains("active") && this.close();
      });

    this.stickyHeaderAvailable = document.querySelector('sticky-header') ? true : false;

    // --- Minimum-order feature internal state ---
    this._minAmount = 300; // 3 KWD = 300 (use same unit your theme uses)
    this._checkoutRestrictionInitialized = false;
    this._checkoutDebounceTimer = null;
    this._mutationObserver = null;

    // Will hold unsubscribe function from pub/sub (if available)
    this._cartUpdateUnsubscriber = null;
  }

  connectedCallback(){
    let stickyHeaderHeight = 0;
    document.querySelectorAll('.section-header').forEach(ele =>{
      stickyHeaderHeight += ele.getBoundingClientRect().height;
    });
    let headerHeight = stickyHeaderHeight;
    document.querySelectorAll('.utility-bar').forEach(ele =>{
      headerHeight += ele.getBoundingClientRect().height;
    });
    document.documentElement.style.setProperty('--header-announce-height', headerHeight+'px');
    document.documentElement.style.setProperty('--header-sticky-height', stickyHeaderHeight+'px');

    // initialize listeners/observer for checkout restriction
    this.initCheckoutRestriction();

    // ------------------------------
    // NEW: subscribe to cart updates published by CartItems
    // so we always get the parsedState (cartData) for remove/update actions.
    // This prevents stale references and ensures the drawer reacts to removes.
    // (subscribe is available in your theme JS because CartItems uses it)
    // ------------------------------
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // event should be the published payload { source, cartData, variantId }
          if (!event) return;

          // Grab the cartData/parsedState if present. Fallback to event itself.
          const parsedState = event.cartData ?? event;

          // Apply restriction immediately with parsedState and confirm with live fetch.
          this.setCheckoutRestriction(parsedState);
          this.debouncedCheckCartTotal();
        });
      }
    } catch (e) {
      // If subscribe/PUB_SUB_EVENTS unavailable, we silently continue — existing listeners still work.
      console.warn('CartDrawer: pubsub subscribe unavailable', e);
    }
  }

  disconnectedCallback() {
    // cleanup subscription if set
    try {
      if (typeof this._cartUpdateUnsubscriber === 'function') {
        this._cartUpdateUnsubscriber();
        this._cartUpdateUnsubscriber = null;
      }
    } catch (e) {
      // ignore
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains("active") ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden-drawer');

    // ensure we have the correct checkout state when drawer opens
    this.debouncedCheckCartTotal();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden-drawer');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      // Apply restriction with parsedState (fast) and always confirm with live fetch
      // (this covers removes/edge cases)
      this.setCheckoutRestriction(parsedState);
      this.debouncedCheckCartTotal();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* -------------------- MINIMUM ORDER / CHECKOUT RESTRICTION -------------------- */

  // Helper: always get the current elements from the DOM (avoid stale references)
  _getCheckoutElements() {
    const checkoutBtn = this.querySelector("#CartDrawer-Checkout") || document.getElementById("CartDrawer-Checkout");
    const messageSpan = this.querySelector("#min-checkout-msg") || document.getElementById("min-checkout-msg");
    return { checkoutBtn, messageSpan };
  }

  // Apply the state to whatever elements exist now.
  _applyCheckoutState(shouldDisable) {
    const { checkoutBtn, messageSpan } = this._getCheckoutElements();
    if (!checkoutBtn || !messageSpan) return;

    if (shouldDisable) {
      // set both property and attribute for robustness
      checkoutBtn.disabled = true;
      checkoutBtn.setAttribute('disabled', '');
      messageSpan.style.display = "block";
    } else {
      checkoutBtn.disabled = false;
      checkoutBtn.removeAttribute('disabled');
      messageSpan.style.display = "none";
    }
  }

  // Disable checkout immediately while waiting for confirmation from server.
  _disableCheckoutDuringUpdate() {
    const { checkoutBtn, messageSpan } = this._getCheckoutElements();
    if (!checkoutBtn) return;
    checkoutBtn.disabled = true;
    checkoutBtn.setAttribute('disabled', '');
    // IMPORTANT: do NOT show the message yet — only show after we confirm it's below min.
    if (messageSpan) messageSpan.style.display = "none";
  }

  /**
   * Use parsedState if available (fast). parsedState might be the cart object
   * (with total_price) or could contain a cart property depending on responses —
   * support both paths. If we don't have a reliable subtotal in parsedState, fall
   * back to fetching /cart.js.
   */
  setCheckoutRestriction(parsedState) {
    const { checkoutBtn, messageSpan } = this._getCheckoutElements();
    if (!checkoutBtn || !messageSpan) return;

    // Initially keep disabled until we confirm (prevents enabling during updates)
    this._disableCheckoutDuringUpdate();

    // Try to read subtotal from parsedState (support both shapes)
    let subtotal;
    if (parsedState) {
      subtotal = parsedState.total_price ?? (parsedState.cart ? parsedState.cart.total_price : undefined);
    }

    if (typeof subtotal !== 'undefined') {
      // parsedState contains subtotal — apply immediately
      const shouldDisable = subtotal < this._minAmount;
      this._applyCheckoutState(shouldDisable);
    } else {
      // fallback to live fetch
      this.checkCartTotal();
    }
  }

  /**
   * Fetch /cart.js to get the latest cart.total_price (Shopify JSON API)
   * and enable/disable checkout button accordingly. Always applies to the
   * current DOM elements (no stale references).
   */
  checkCartTotal() {
    // Keep checkout disabled while waiting for the server
    this._disableCheckoutDuringUpdate();

    fetch('/cart.js', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Network error');
        return response.json();
      })
      .then((cart) => {
        if (!cart || typeof cart.total_price === 'undefined') {
          // safe fallback: keep disabled and show message
          this._applyCheckoutState(true);
          return;
        }

        const shouldDisable = cart.total_price < this._minAmount;
        this._applyCheckoutState(shouldDisable);
      })
      .catch((err) => {
        console.error('checkCartTotal error:', err);
        // safest fallback: keep disabled and show message
        this._applyCheckoutState(true);
      });
  }

  debouncedCheckCartTotal() {
    clearTimeout(this._checkoutDebounceTimer);
    this._checkoutDebounceTimer = setTimeout(() => this.checkCartTotal(), 150);
  }

  initCheckoutRestriction() {
    if (this._checkoutRestrictionInitialized) return;
    this._checkoutRestrictionInitialized = true;

    // When inputs change (quantity inputs), trigger a debounced fetch and disable immediately.
    this.addEventListener('change', (e) => {
      // matches number inputs, quantity inputs, selects etc.
      if (e.target && e.target.matches && e.target.matches('input[type="number"], input.quantity, select')) {
        this._disableCheckoutDuringUpdate();
        this.debouncedCheckCartTotal();
      }
    }, { passive: true });

    // input events (typing into number fields)
    this.addEventListener('input', (e) => {
      if (e.target && e.target.matches && e.target.matches('input[type="number"], input.quantity')) {
        this._disableCheckoutDuringUpdate();
        this.debouncedCheckCartTotal();
      }
    }, { passive: true });

    // clicks — many qty UIs use buttons; act only when click is inside qty area
    this.addEventListener('click', (e) => {
      const btn = e.target;
      if (!btn) return;
      if (btn.closest && (btn.closest('.quantity') || btn.closest('.cart__qty') || btn.closest('.cart__remove'))) {
        this._disableCheckoutDuringUpdate();
        this.debouncedCheckCartTotal();
      }
    });

    // MutationObserver: fallback for programmatic DOM updates and replacements.
    const observeRoot = this.querySelector('.drawer__inner') || this;
    try {
      this._mutationObserver = new MutationObserver((mutations) => {
        // If mutations contain removal/addition of nodes, treat as update
        let relevant = false;
        for (const m of mutations) {
          if (m.addedNodes.length || m.removedNodes.length || m.type === 'childList') {
            relevant = true;
            break;
          }
        }
        if (relevant) {
          this._disableCheckoutDuringUpdate();
          this.debouncedCheckCartTotal();
        }
      });
      this._mutationObserver.observe(observeRoot, {
        childList: true,
        subtree: true,
      });
    } catch (e) {
      console.warn('MutationObserver unavailable for cart drawer restriction', e);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
